import { FeatureAccessSchema } from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/components/ui/toaster';
import { usePermissions } from '@/hooks/use-permissions';
import { currentUserKeys } from '@/queries/current-user';
import { ApiService } from '@/services/api-v2';
import { useUserStore } from '@/stores/user.store';

// The wire schema marks every flag optional (partial updates); the form needs a
// concrete boolean per flag for the controlled Switch, so require them here.
const formSchema = FeatureAccessSchema.required();

function SettingsFeatureAccessPage() {
  return (
    <div className="flex w-full max-w-[833px] flex-col gap-4 rounded-lg bg-white p-6 shadow-lg">
      <ScrollArea viewportClassName="pr-2">
        <div className="flex flex-col gap-4">
          <h1 className="text-default-text text-xl font-medium">Feature Access</h1>
          <SettingsFeatureAccessForm />
        </div>
      </ScrollArea>
    </div>
  );
}

function SettingsFeatureAccessForm() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { isSuperUser } = usePermissions();
  const { org, setOrg } = useUserStore();

  const formInstance = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      enableConditionalLogic: org?.featureAccess?.enableConditionalLogic ?? false,
    },
  });
  const {
    control,
    handleSubmit,
    formState: { isValid, isSubmitting, isDirty },
  } = formInstance;

  const { mutateAsync: updateFeatureAccess } = useMutation({
    mutationFn: (data: z.infer<typeof formSchema>) => {
      if (!org) {
        throw new Error('No workspace selected');
      }

      return ApiService.UpdateWorkspaceFeatureAccess({
        workspaceId: org.id,
        featureAccess: data,
      });
    },
    onSuccess: (_, submittedData) => {
      formInstance.reset(submittedData);
      if (org) {
        setOrg({ ...org, featureAccess: { ...org.featureAccess, ...submittedData } });
      }
      queryClient.invalidateQueries({ queryKey: currentUserKeys.all });
      toast.success('Feature access updated successfully');
    },
    onError: () => {
      toast.error('Failed to update feature access');
    },
  });

  async function onSubmit(data: z.infer<typeof formSchema>) {
    await updateFeatureAccess(data);
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
      <p className="text-default-text text-sm">
        These settings allow you to control the features that are enabled for a workspace. If you wish to gain access to any of the features listed in this
        page, please talk to your Sitemate Account Manager to learn more.
      </p>

      {isSuperUser && (
        <Controller
          control={control}
          name="enableConditionalLogic"
          render={({ field }) => (
            <Label className="flex items-center gap-1">
              <div className="w-2/5 shrink-0">Conditional Logic</div>
              <Switch checked={field.value} onCheckedChange={field.onChange} disabled={!isSuperUser} />
            </Label>
          )}
        />
      )}

      {isSuperUser && (
        <div className="col-12">
          <Button type="submit" variant="success" className="mt-2" disabled={!isValid || isSubmitting || !isDirty} loading={isSubmitting}>
            Save changes
          </Button>
        </div>
      )}
    </form>
  );
}

export default SettingsFeatureAccessPage;
